/**
 * app.tsx — Provider portal router.
 *
 * Story 1.6: Cognito provider authentication.
 * Story 7.7: Provider T&C Agreement System — Onboarding Hard Gate.
 *
 * Route structure:
 * - /auth/callback   — Cognito OAuth callback
 * - /auth/logout     — Logout complete page
 * - /terms-agreement — T&C acceptance page (authenticated, NOT behind TcVersionGuardRoute)
 * - /                — RequireAuth wraps all authenticated routes
 *   - TcVersionGuardRoute wraps all portal routes (except /terms-agreement)
 *     - /members, /dashboard, etc.
 */

import { AuthCallback, RequireAuth, usePortalAuth } from '@nrx/auth/react';
import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { Navigate, Outlet, RouterProvider, createBrowserRouter } from 'react-router-dom';

import { PortalLayout } from './components/portal-layout.js';
import { MemberListPage } from './features/member-list/member-list-page.js';
import { TcAgreementPage } from './features/tc-agreement/TcAgreementPage.js';
import { TcVersionGuardRoute } from './features/tc-agreement/TcVersionGuardRoute.js';
import { Forbidden } from './pages/auth/forbidden.js';
import { LogoutComplete } from './pages/auth/logout-complete.js';

/**
 * LayoutShell — renders PortalLayout (header with Sign Out button) around nested routes.
 * This is a layout route element: it renders <Outlet /> inside the portal shell so that
 * child routes (members, etc.) are presented within the header/nav.
 */
function LayoutShell(): JSX.Element {
  return (
    <PortalLayout>
      <Outlet />
    </PortalLayout>
  );
}

/**
 * TcAgreementPageWrapper — renders TcAgreementPage with the providerId.
 *
 * Primary source: `custom:providerId` from the Cognito ID token (OIDC profile).
 * Fallback: GET /v1/providers/tc/my-status (resolves providerId via cognitoSub)
 * for providers whose Cognito user was not created with the custom attribute set.
 */
function TcAgreementPageWrapper(): JSX.Element {
  const auth = usePortalAuth();
  const [providerId, setProviderId] = useState<string | null>(
    (auth.user?.profile?.['custom:providerId'] as string | undefined) ?? null,
  );
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    // If custom:providerId was already available from the ID token, no fetch needed.
    if (providerId) return;
    if (!auth.isAuthenticated) return;

    // Fallback: resolve via my-status endpoint (uses cognitoSub link set at first acceptance).
    fetch('/v1/providers/tc/my-status', {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })
      .then((res) => (res.ok ? (res.json() as Promise<{ providerId: string | null }>) : null))
      .then((status) => {
        if (status?.providerId) {
          setProviderId(status.providerId);
        } else {
          setLoadError(true);
        }
      })
      .catch(() => setLoadError(true));
  }, [auth.isAuthenticated, providerId]);

  if (loadError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <p className="text-gray-500">Could not load your profile. Please try signing in again.</p>
      </div>
    );
  }

  if (!providerId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <p className="text-gray-500">Loading your profile…</p>
      </div>
    );
  }

  return <TcAgreementPage providerId={providerId} />;
}

const router = createBrowserRouter([
  // Public routes
  {
    path: '/auth/callback',
    element: <AuthCallback />,
  },
  {
    path: '/auth/logout',
    element: <LogoutComplete />,
  },
  // Auth guard — RequireAuth renders <Outlet /> for authenticated users
  {
    path: '/',
    element: <RequireAuth />,
    children: [
      // T&C agreement page — authenticated but NOT behind TcVersionGuardRoute
      // (providers must be able to reach this even when T&C is out-of-date)
      {
        path: 'terms-agreement',
        element: <TcAgreementPageWrapper />,
      },

      // TcVersionGuardRoute — wraps all other authenticated routes
      // Redirects to /terms-agreement if T&C version is behind current
      {
        element: <TcVersionGuardRoute />,
        children: [
          // Portal shell layout — header with Sign Out button
          {
            element: <LayoutShell />,
            children: [
              {
                index: true,
                element: <Navigate to="/members" replace />,
              },
              {
                path: 'members',
                element: <MemberListPage />,
              },
              {
                path: 'dashboard',
                element: <MemberListPage />, // placeholder — full dashboard in Story 7.3
              },
              {
                path: '*',
                element: <Forbidden />,
              },
            ],
          },
        ],
      },
    ],
  },
]);

export function App(): JSX.Element {
  return <RouterProvider router={router} />;
}
